'use client';

import { useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';

const BookingConversions = () => {
  const conversionFunnelData = useMemo(() => [
    { stage: 'Search', users: 2615, percentage: 100 },
    { stage: 'View Results', users: 2489, percentage: 95.2 },
    { stage: 'Select Flight', users: 1876, percentage: 71.7 },
    { stage: 'Click Book', users: 892, percentage: 34.1 },
    { stage: 'Complete Booking', users: 400, percentage: 15.3 },
  ], []);

  const conversionRateData = useMemo(() => [
    { date: 'Week 1', rate: 14.2 },
    { date: 'Week 2', rate: 15.8 },
    { date: 'Week 3', rate: 14.9 },
    { date: 'Week 4', rate: 15.3 },
  ], []);

  const partnerPerformanceData = useMemo(() => [
    { partner: 'Skyscanner', clicks: 456, conversions: 89, revenue: 2847 },
    { partner: 'Booking.com', clicks: 389, conversions: 76, revenue: 2456 },
    { partner: 'Expedia', clicks: 312, conversions: 58, revenue: 1923 },
  ], []);

  const stats = [
    { label: 'Conversion Rate', value: '15.3%', change: '+1.1%', icon: 'ChartBarIcon', trend: 'up' },
    { label: 'Total Bookings', value: '400', change: '+32', icon: 'CheckCircleIcon', trend: 'up' },
    { label: 'Avg. Booking Value', value: '£847', change: '+£23', icon: 'CurrencyPoundIcon', trend: 'up' },
    { label: 'Drop-off Rate', value: '84.7%', change: '-1.1%', icon: 'ArrowTrendingDownIcon', trend: 'up' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-card rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name={stat.icon as any} size={24} className="text-primary" />
              <span className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-success' : 'text-error'
              }`}>
                {stat.change}
              </span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
            <div className="text-sm text-muted-foreground">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Conversion Funnel */}
      <div className="bg-card rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Conversion Funnel</h3>
        <div className="space-y-3">
          {conversionFunnelData.map((stage, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground">{stage.stage}</span>
                <span className="text-sm text-muted-foreground">
                  {stage.users.toLocaleString()} users ({stage.percentage}%)
                </span>
              </div>
              <div className="w-full bg-surface rounded-full h-3">
                <div
                  className="bg-primary rounded-full h-3 transition-all duration-500"
                  style={{ width: `${stage.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Rate Trend */}
        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Conversion Rate Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={conversionRateData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="rate" stroke="#10B981" strokeWidth={2} name="Conversion Rate (%)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Partner Performance */}
        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Partner Performance</h3>
          <div className="space-y-4">
            {partnerPerformanceData.map((partner, index) => (
              <div key={index} className="border-b border-border pb-3 last:border-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground">{partner.partner}</span>
                  <span className="text-sm text-success font-medium">£{partner.revenue.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{partner.clicks} clicks</span>
                  <span>{partner.conversions} conversions</span>
                  <span>{((partner.conversions / partner.clicks) * 100).toFixed(1)}% rate</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConversions;